import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  ArrowLeft,
  Menu,
  Layers3,
  PanelLeftClose,
  PanelLeftOpen
} from 'lucide-react'
import { Button } from '../ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../ui/dropdown-menu'
import { cn } from '../../lib/utils'
import { ADMIN_NAV_ITEMS, type AdminNavItem } from '../../config/navigation'

type AdminNavProps = {
  items?: AdminNavItem[]
}

const AdminNav = ({ items = ADMIN_NAV_ITEMS }: AdminNavProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.localStorage.getItem('admin_nav_collapsed') === '1'
  })

  useEffect(() => {
    const syncCollapsed = (event: Event) => {
      const custom = event as CustomEvent<{ collapsed?: boolean }>
      if (typeof custom.detail?.collapsed === 'boolean') {
        setCollapsed(custom.detail.collapsed)
        return
      }
      setCollapsed(window.localStorage.getItem('admin_nav_collapsed') === '1')
    }

    window.addEventListener('admin-nav-collapsed-changed', syncCollapsed)
    return () => {
      window.removeEventListener('admin-nav-collapsed-changed', syncCollapsed)
    }
  }, [])

  const toggleCollapsed = () => {
    const next = !collapsed
    setCollapsed(next)
    window.localStorage.setItem('admin_nav_collapsed', next ? '1' : '0')
    window.dispatchEvent(
      new CustomEvent('admin-nav-collapsed-changed', { detail: { collapsed: next } })
    )
  }

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`)
  }

  const goToUserPage = () => {
    setOpen(false)
    navigate('/')
  }

  return (
    <>
      <div className="border-b border-slate-200 bg-white/95 backdrop-blur-md md:hidden">
        <div className="mx-auto w-full max-w-[1400px] px-4 py-3 md:px-6">
          <div className="flex items-center justify-between">
            <button type="button" onClick={() => navigate('/admin/dashboard')} className="text-sm font-semibold text-slate-700">Oreun 관리자</button>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="gap-1.5 px-2 text-xs text-slate-600"
                onClick={goToUserPage}
              >
                <ArrowLeft className="h-4 w-4" />
                사용자 페이지
              </Button>
              <DropdownMenu open={open} onOpenChange={setOpen}>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="관리자 메뉴">
                    <Menu className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  {items.map((item) => (
                    <DropdownMenuItem
                      key={item.path}
                      className={cn(isActive(item.path) && 'bg-slate-100 text-slate-900')}
                      onClick={() => {
                        navigate(item.path)
                        setOpen(false)
                      }}
                    >
                      {item.label}
                    </DropdownMenuItem>
                  ))}
                  <div className="mt-1 border-t border-slate-200 pt-1">
                    <DropdownMenuItem onClick={goToUserPage} className="gap-2 font-medium text-slate-700">
                      <ArrowLeft className="h-4 w-4" />
                      사용자 페이지로 돌아가기
                    </DropdownMenuItem>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>

      <aside
        className={cn(
          'fixed left-0 top-0 z-30 hidden h-screen border-r border-slate-200 bg-white/95 px-3 py-4 backdrop-blur-md transition-all duration-200 md:block',
          collapsed ? 'w-[84px]' : 'w-[260px]'
        )}
      >
        <div className={cn('mb-4 flex min-h-10 items-center', collapsed ? 'justify-center' : 'justify-between px-2')}>
          {!collapsed && (
            <button type="button" onClick={() => navigate('/admin/dashboard')} className="text-left">
              <span className="block text-sm font-bold text-slate-900">Oreun Academy</span>
              <span className="block text-xs text-slate-500">관리자</span>
            </button>
          )}
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            onClick={toggleCollapsed}
            aria-label={collapsed ? '사이드 메뉴 펼치기' : '사이드 메뉴 접기'}
          >
            {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </Button>
        </div>
        <div className="h-[calc(100vh-8.5rem)] space-y-1 overflow-y-auto pb-4">
          {items.map((item) => {
            const Icon = item.icon || Layers3
            const active = isActive(item.path)

            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                title={collapsed ? item.label : undefined}
                className={cn(
                  'flex w-full items-center rounded-xl px-3 py-2.5 text-sm font-medium transition',
                  collapsed ? 'justify-center' : 'gap-2.5',
                  active
                    ? 'bg-[#e8f3ff] text-[#0064ff]'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                )}
              >
                <Icon className="h-4 w-4 shrink-0" />
                {!collapsed && <span className="truncate text-left">{item.label}</span>}
              </button>
            )
          })}
        </div>
        <div className="border-t border-slate-200 pt-3">
          <button
            type="button"
            onClick={goToUserPage}
            title={collapsed ? '사용자 페이지로 돌아가기' : undefined}
            aria-label={collapsed ? '사용자 페이지로 돌아가기' : undefined}
            className={cn(
              'flex w-full items-center rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700',
              collapsed ? 'justify-center' : 'gap-2.5'
            )}
          >
            <ArrowLeft className="h-4 w-4 shrink-0" />
            {!collapsed && <span>사용자 페이지로 돌아가기</span>}
          </button>
        </div>
      </aside>
    </>
  )
}

export default AdminNav
